/**
 * Pay app API — submit, PDF URL, email (v4).
 */

import { apiFetch, getToken, resolveApiUrl } from './client'

export type SubmitPayAppInput = {
  period_label?: string
  period_start?: string
  period_end?: string
  po_number?: string
  special_notes?: string
  status: 'submitted' | 'draft'
}

export async function submitPayApp(
  payAppId: number,
  data: SubmitPayAppInput,
): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>('PUT', `/api/payapps/${payAppId}`, data)
}

/** Build PDF download URL with JWT query param (open in new tab). */
export function getPayAppPdfUrl(payAppId: number, token: string): string {
  const q = encodeURIComponent(token)
  return resolveApiUrl(`/api/payapps/${payAppId}/pdf?token=${q}`)
}

export async function emailPayApp(
  payAppId: number,
  data: {
    to_email: string
    to_name?: string
    message?: string
    subject?: string
    body_html?: string
    aria_followup?: boolean
    lines?: Array<{ id: number; this_pct?: number; retainage_pct?: number; stored_materials?: number }>
  },
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('POST', `/api/payapps/${payAppId}/email`, {
    to: data.to_email,
    ...(data.subject ? { subject: data.subject } : {}),
    ...(data.body_html ? { body_html: data.body_html } : {}),
    ...(data.aria_followup ? { aria_followup: true } : {}),
    ...(data.message ? { message: data.message } : {}),
    ...(data.lines?.length ? { lines: data.lines } : {}),
  })
}

export async function createChangeOrder(
  payAppId: number,
  data: { description: string; amount: number; sov_line_id?: number | null; hold_retainage?: boolean },
): Promise<{ id: number; description: string; amount: string; co_number: number; status: string; sov_line_id?: number | null }> {
  return apiFetch('POST', `/api/payapps/${payAppId}/changeorders`, data)
}

export async function deleteChangeOrder(id: number): Promise<void> {
  await apiFetch('DELETE', `/api/changeorders/${id}`)
}

export type DeletePayAppWarning = {
  warning: true
  message: string
  subsequent: Array<{ id: number; app_number: number; period_label: string | null; status: string }>
  target: { id: number; app_number: number }
}

export type DeletePayAppSuccess = {
  ok: true
  deleted_count: number
  app_numbers: number[]
}

export type DeletePayAppResult = DeletePayAppSuccess | DeletePayAppWarning

/** Soft-delete pay app; returns cascade warning on 409 unless cascade=true */
export async function deletePayApp(id: number, cascade = false): Promise<DeletePayAppResult> {
  const token = getToken()
  const qs = cascade ? '?cascade=true' : ''
  const res = await fetch(resolveApiUrl(`/api/payapps/${id}${qs}`), {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  const body = await res.json().catch(() => ({})) as Record<string, unknown>
  if (res.status === 409 && body.warning === true) {
    return body as unknown as DeletePayAppWarning
  }
  if (!res.ok) {
    throw new Error(
      (typeof body.error === 'string' && body.error)
        || (typeof body.message === 'string' && body.message)
        || `HTTP ${res.status}`,
    )
  }
  return body as unknown as DeletePayAppSuccess
}

export async function restorePayApp(id: number): Promise<{ ok: boolean }> {
  return apiFetch('POST', `/api/payapps/${id}/restore`, {})
}

export { getToken }
